import { Schema as S } from "effect";

/**
 * Allowed demographic gender value.
 * @since 1.1.11
 * @example
 * const value = Gender.make("unknown");
 */
export const Gender = S.Literals(["male", "female", "other", "unknown"]);
/**
 * Decoded value of {@link Gender}.
 * @since 1.1.11
 * @example
 * const value: Gender = "unknown";
 */
export type Gender = S.Schema.Type<typeof Gender>;

/**
 * Insurance category carried by the card workflow.
 * @since 1.1.11
 * @example
 * const value = InsuranceType.make("statutory");
 */
export const InsuranceType = S.Literals(["statutory", "private", "self-pay"]);
/**
 * Decoded value of {@link InsuranceType}.
 * @since 1.1.11
 * @example
 * const value: InsuranceType = "statutory";
 */
export type InsuranceType = S.Schema.Type<typeof InsuranceType>;

/**
 * Postal address carried by the card workflow.
 * @since 1.1.11
 * @example
 * const value = Address.make({ street: "", houseNumber: "", zip: "", city: "", country: "DE" });
 */
export const Address = S.Struct({
  street: S.String,
  houseNumber: S.String,
  zip: S.String,
  city: S.String,
  country: S.String,
});
/**
 * Decoded value of {@link Address}.
 * @since 1.1.11
 * @example
 * const value: Address = { street: "", houseNumber: "", zip: "", city: "", country: "DE" };
 */
export type Address = S.Schema.Type<typeof Address>;

/**
 * Insurance information returned by a card read.
 * @since 1.1.11
 * @example
 * const value = InsuranceInfo.make({ insuranceId: "", insuranceName: "", insuranceType: "statutory", validFrom: "" });
 */
export const InsuranceInfo = S.Struct({
  /** Patient insurance number (KVNR) read from the eGK. */
  insuranceId: S.String,
  /** Cost-carrier IK from the eGK, used only when creating the insurance record. */
  insurerId: S.optional(S.String),
  insuranceName: S.String,
  insuranceType: InsuranceType,
  validFrom: S.String,
});
/**
 * Decoded value of {@link InsuranceInfo}.
 * @since 1.1.11
 * @example
 * const value: InsuranceInfo = { insuranceId: "", insuranceName: "", insuranceType: "statutory", validFrom: "" };
 */
export type InsuranceInfo = S.Schema.Type<typeof InsuranceInfo>;

/**
 * Demographics carried by a usable card-read result.
 * @since 1.1.11
 * @example
 * const value = PatientDemographicData.make({ givenName: "", familyName: "", birthDate: "", gender: "unknown", insurance: { insuranceId: "", insuranceName: "", insuranceType: "statutory", validFrom: "" } });
 */
export const PatientDemographicData = S.Struct({
  givenName: S.String,
  familyName: S.String,
  birthDate: S.String,
  gender: Gender,
  address: S.optional(Address),
  insurance: InsuranceInfo,
});
/**
 * Decoded value of {@link PatientDemographicData}.
 * @since 1.1.11
 * @example
 * const value: PatientDemographicData = { givenName: "", familyName: "", birthDate: "", gender: "unknown", insurance: { insuranceId: "", insuranceName: "", insuranceType: "statutory", validFrom: "" } };
 */
export type PatientDemographicData = S.Schema.Type<typeof PatientDemographicData>;
